import { cn } from "@/utils";
import React, {
  MouseEventHandler,
  useCallback,
  useEffect,
  useRef,
} from "react";
import { MdClose } from "react-icons/md";

type DrawerSize = "small" | "medium" | "large" | "full";

type DrawerProps = {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: DrawerSize;
  label?: string;
  description?: string;
  hideCloseButton?: boolean;
  disableBackdropClick?: boolean;
  placement?: "left" | "right";
  hideHeader?: boolean;
  modeRender?: "use-css" | "return-null";
};

const Drawer: React.FC<DrawerProps> = ({
  open,
  onClose,
  children,
  size,
  label,
  description,
  hideCloseButton,
  disableBackdropClick,
  placement = "right",
  hideHeader = false,
  modeRender = "return-null",
}) => {
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [open]);

  const handleClickOutside: MouseEventHandler<HTMLDivElement> = (event) => {
    if (disableBackdropClick) return;

    if (
      contentRef.current &&
      !contentRef.current.contains(event.target as Node)
    ) {
      onClose();
    }
  };

  const getSizeClass = useCallback((size: DrawerSize) => {
    const style = {
      small: "w-full md:w-1/5",
      medium: "w-full md:w-2/5",
      large: "w-full md:w-4/5 lg:w-3/5 2xl:w-2/5",
      full: "w-full",
    };
    return style[size] || style.full;
  }, []);

  // Check key press to close drawer
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      onClose();
    }
  };
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!open && modeRender === "return-null") return null;

  return (
    <div
      className="fixed z-50 inset-0 bg-black bg-opacity-80"
      style={{
        display: modeRender === "use-css" ? (open ? "block" : "none") : "block",
      }}
      onClick={handleClickOutside}
    >
      <div
        ref={contentRef}
        className={cn(
          "fixed top-0 bg-white shadow-shadow_light_card z-50 flex flex-col h-[100vh] overflow-hidden",
          getSizeClass(size || "medium"),
          placement === "left"
            ? "left-0 right-auto border-r"
            : "right-0 left-auto border-l"
        )}
      >
        {!hideHeader && (
          <div className="flex items-center justify-between border-b p-4 text-lg font-bold leading-7">
            <div className="space-y-1">
              {label}
              {description && (
                <p className="text-xs font-normal text-light_secondary leading-4.5 !whitespace-pre-line">
                  {description}
                </p>
              )}
            </div>
            {!hideCloseButton && (
              <MdClose
                className="cursor-pointer hover:opacity-70 transition-opacity duration-300 size-6"
                onClick={onClose}
              />
            )}
          </div>
        )}
        {/* In mobile view, make content scrollable  */}
        <div className="flex-1 overflow-auto md:overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
};

function DrawerFooter({ children }: { children: React.ReactNode }) {
  return (
    <div className="border-t p-4 flex items-center justify-end gap-4">
      {children}
    </div>
  );
}

export { Drawer, DrawerFooter };
